import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  IndianRupee, Calendar, AlertTriangle, CheckCircle2, Clock,
  Building2, CreditCard, Edit, RefreshCw, CalendarIcon, User, Filter, TrendingUp,
  Info, Copy, Landmark
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

// Timezone-safe date formatting (returns YYYY-MM-DD in local timezone)
const toLocalDateString = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getPriorityBadge = (priority) => {
  const styles = {
    normal: { className: 'bg-gray-100 text-gray-700', label: 'Normal' },
    urgent: { className: 'bg-amber-100 text-amber-700', label: 'Urgent' },
    critical: { className: 'bg-red-100 text-red-700 animate-pulse', label: 'Critical' },
  };
  const style = styles[priority] || styles.normal;
  return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
};

const getStatusBadge = (request) => {
  const today = toLocalDateString(new Date());
  const dueDate = request.approved_payment_date;
  
  if (dueDate < today) {
    return <Badge variant="destructive">Overdue</Badge>;
  } else if (dueDate === today) {
    return <Badge className="bg-amber-500">Due Today</Badge>;
  } else {
    const tomorrow = toLocalDateString(new Date(Date.now() + 86400000));
    if (dueDate === tomorrow) {
      return <Badge className="bg-blue-500">Tomorrow</Badge>;
    }
  }
  return <Badge variant="outline">Upcoming</Badge>;
};

// Copy to clipboard helper
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied to clipboard`);
  }).catch(() => {
    toast.error('Failed to copy');
  });
};

// Bank Details Popover Component
const BankDetailsPopover = ({ supplier }) => {
  if (!supplier) return null;
  
  const bankDetails = supplier.bank_details || {};
  const hasBank = bankDetails.bank_name || bankDetails.account_number || bankDetails.ifsc_code;
  const hasGstin = supplier.gst_number;
  
  if (!hasBank && !hasGstin) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-300" disabled>
              <Info className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>No bank details available</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50">
          <Landmark className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {supplier.name}
          </h4>
          
          {hasGstin && (
            <div className="p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">GSTIN</p>
                  <p className="font-mono text-sm font-medium">{supplier.gst_number}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  onClick={() => copyToClipboard(supplier.gst_number, 'GSTIN')}
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
          
          {hasBank && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium">Bank Details</p>
              
              {bankDetails.bank_name && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="text-xs text-gray-500">Bank</p>
                    <p className="text-sm">{bankDetails.bank_name}</p>
                  </div>
                </div>
              )}
              
              {bankDetails.account_number && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="text-xs text-gray-500">Account Number</p>
                    <p className="font-mono text-sm">{bankDetails.account_number}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0"
                    onClick={() => copyToClipboard(bankDetails.account_number, 'Account number')}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              
              {bankDetails.ifsc_code && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="text-xs text-gray-500">IFSC Code</p>
                    <p className="font-mono text-sm">{bankDetails.ifsc_code}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0"
                    onClick={() => copyToClipboard(bankDetails.ifsc_code, 'IFSC code')}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              
              {bankDetails.branch && (
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">Branch</p>
                  <p className="text-sm">{bankDetails.branch}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function AccountsPaymentsPage() {
  const [upcomingData, setUpcomingData] = useState({
    overdue: [],
    grouped_by_date: {},
    total_due: 0,
    total_overdue: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Payment dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: toLocalDateString(new Date()),
    amount_paid: '',
    payment_mode: 'neft',
    reference_no: '',
    cheque_no: '',
    remarks: ''
  });
  const [processing, setProcessing] = useState(false);
  
  // Modification dialog
  const [modDialogOpen, setModDialogOpen] = useState(false);
  const [modForm, setModForm] = useState({
    modification_type: 'change_date',
    new_amount: '',
    new_date: '',
    reason: ''
  });

  // Filter state
  const [requesterFilter, setRequesterFilter] = useState('all');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('upcoming');

  // Suppliers data for bank details
  const [suppliersMap, setSuppliersMap] = useState({});

  const fetchUpcomingPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/payment-requests/accounts/upcoming?days=60`, {
        headers: getAuthHeader()
      });
      setUpcomingData(response.data);
      
      // Fetch supplier details for bank info
      const supplierIds = [...new Set([
        ...response.data.overdue.map(r => r.supplier_id),
        ...Object.values(response.data.grouped_by_date).flat().map(r => r.supplier_id)
      ])].filter(Boolean);
      
      if (supplierIds.length > 0) {
        const suppliersResp = await axios.get(`${API_URL}/api/suppliers`, {
          headers: getAuthHeader()
        });
        const suppMap = {};
        (suppliersResp.data || []).forEach(s => {
          suppMap[s.id] = s;
        });
        setSuppliersMap(suppMap);
      }
    } catch (error) {
      console.error('Error fetching upcoming payments:', error);
      toast.error('Failed to load upcoming payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpcomingPayments();
  }, [fetchUpcomingPayments]);

  const openPayDialog = (request) => {
    setSelectedRequest(request);
    setPaymentForm({
      payment_date: toLocalDateString(new Date()),
      amount_paid: request.approved_amount,
      payment_mode: 'neft',
      reference_no: '',
      cheque_no: '',
      remarks: ''
    });
    setPayDialogOpen(true);
  };

  const openModDialog = (request) => {
    setSelectedRequest(request);
    setModForm({
      modification_type: 'change_date',
      new_amount: request.approved_amount,
      new_date: request.approved_payment_date,
      reason: ''
    });
    setModDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount_paid || !paymentForm.payment_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    if (paymentForm.payment_mode !== 'cash' && !paymentForm.reference_no && !paymentForm.cheque_no) {
      toast.error('Please enter reference/cheque number');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${selectedRequest.id}/record-payment`,
        {
          payment_date: paymentForm.payment_date,
          amount_paid: parseFloat(paymentForm.amount_paid),
          payment_mode: paymentForm.payment_mode,
          reference_no: paymentForm.reference_no || null,
          cheque_no: paymentForm.cheque_no || null,
          remarks: paymentForm.remarks || null
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Payment recorded successfully');
      setPayDialogOpen(false);
      fetchUpcomingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestModification = async () => {
    if (!modForm.reason) {
      toast.error('Please provide a reason');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${selectedRequest.id}/request-modification`,
        {
          modification_type: modForm.modification_type,
          new_amount: modForm.modification_type !== 'change_date' ? parseFloat(modForm.new_amount) : null,
          new_date: modForm.modification_type !== 'change_amount' ? modForm.new_date : null,
          reason: modForm.reason
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Modification requested');
      setModDialogOpen(false);
      fetchUpcomingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request modification');
    } finally {
      setProcessing(false);
    }
  };

  // Flatten all payments into a single sorted list
  // Get unique requesters for filter dropdown
  const getUniqueRequesters = () => {
    const requesters = new Set();
    
    upcomingData.overdue.forEach(req => {
      if (req.created_by_name) requesters.add(req.created_by_name);
    });
    
    Object.values(upcomingData.grouped_by_date).forEach(requests => {
      requests.forEach(req => {
        if (req.created_by_name) requesters.add(req.created_by_name);
      });
    });
    
    return Array.from(requesters).sort();
  };

  const uniqueRequesters = getUniqueRequesters();

  const getAllPayments = () => {
    const today = toLocalDateString(new Date());
    const allPayments = [];
    
    // Add overdue payments
    upcomingData.overdue.forEach(req => {
      allPayments.push({ ...req, isOverdue: true });
    });
    
    // Add upcoming payments from grouped_by_date
    Object.keys(upcomingData.grouped_by_date).sort().forEach(dateStr => {
      upcomingData.grouped_by_date[dateStr].forEach(req => {
        // Avoid duplicates (overdue payments might be in both)
        if (!allPayments.find(p => p.id === req.id)) {
          allPayments.push({ ...req, isOverdue: dateStr < today });
        }
      });
    });
    
    // Apply requester filter
    const filteredPayments = requesterFilter === 'all' 
      ? allPayments 
      : allPayments.filter(p => p.created_by_name === requesterFilter);
    
    // Sort by due date (earliest first), then by priority
    return filteredPayments.sort((a, b) => {
      // Overdue first
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      // Then by date
      if (a.approved_payment_date !== b.approved_payment_date) {
        return a.approved_payment_date.localeCompare(b.approved_payment_date);
      }
      // Then by priority
      const priorityOrder = { critical: 0, urgent: 1, normal: 2 };
      return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
    });
  };

  const allPayments = getAllPayments();

  // Get aging analysis for overdue payments
  const getAgingAnalysis = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const agingBuckets = {
      '1-7 days': { count: 0, amount: 0, items: [] },
      '8-15 days': { count: 0, amount: 0, items: [] },
      '16-30 days': { count: 0, amount: 0, items: [] },
      '31-60 days': { count: 0, amount: 0, items: [] },
      '61-90 days': { count: 0, amount: 0, items: [] },
      '90+ days': { count: 0, amount: 0, items: [] }
    };
    
    upcomingData.overdue.forEach(req => {
      const dueDate = new Date(req.approved_payment_date);
      dueDate.setHours(0, 0, 0, 0);
      const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      
      let bucket;
      if (daysOverdue <= 7) bucket = '1-7 days';
      else if (daysOverdue <= 15) bucket = '8-15 days';
      else if (daysOverdue <= 30) bucket = '16-30 days';
      else if (daysOverdue <= 60) bucket = '31-60 days';
      else if (daysOverdue <= 90) bucket = '61-90 days';
      else bucket = '90+ days';
      
      agingBuckets[bucket].count++;
      agingBuckets[bucket].amount += req.approved_amount;
      agingBuckets[bucket].items.push({
        ...req,
        daysOverdue
      });
    });
    
    return agingBuckets;
  };

  const agingData = getAgingAnalysis();
  const totalOverdueAmount = Object.values(agingData).reduce((sum, b) => sum + b.amount, 0);
  const totalOverdueCount = Object.values(agingData).reduce((sum, b) => sum + b.count, 0);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="accounts-payments-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payment Management</h1>
            <p className="text-muted-foreground">Manage and record supplier payments</p>
          </div>
          <Button variant="outline" onClick={fetchUpcomingPayments}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              <CreditCard className="w-4 h-4 mr-2" />
              Upcoming Payments
            </TabsTrigger>
            <TabsTrigger value="aging" data-testid="tab-aging">
              <TrendingUp className="w-4 h-4 mr-2" />
              Aging Report ({totalOverdueCount})
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Payments Tab */}
          <TabsContent value="upcoming" className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-3 justify-end">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={requesterFilter} onValueChange={setRequesterFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="requester-filter">
                    <SelectValue placeholder="Filter by Requester" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Requesters</SelectItem>
                    {uniqueRequesters.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

        {/* Summary Cards - Compact */}
        {(() => {
          // Helper to calculate totals for different date ranges
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayStr = toLocalDateString(today);
          
          // End of this week (Sunday) - if today is Saturday (6), Sunday is tomorrow
          const endOfThisWeek = new Date(today);
          const daysUntilSunday = today.getDay() === 0 ? 0 : (7 - today.getDay());
          endOfThisWeek.setDate(today.getDate() + daysUntilSunday);
          const endOfThisWeekStr = toLocalDateString(endOfThisWeek);
          
          // Next week: Monday after this Sunday to next Sunday
          const nextWeekStart = new Date(endOfThisWeek);
          nextWeekStart.setDate(nextWeekStart.getDate() + 1); // Monday
          const nextWeekStartStr = toLocalDateString(nextWeekStart);
          
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekStart.getDate() + 6); // Sunday
          const nextWeekEndStr = toLocalDateString(nextWeekEnd);
          
          // End of current calendar month
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          const endOfMonthStr = toLocalDateString(endOfMonth);
          
          // Start of current month (for "Due This Month" - payments in current month only)
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const startOfMonthStr = toLocalDateString(startOfMonth);
          
          // Next 30 days from today
          const next30Days = new Date(today);
          next30Days.setDate(today.getDate() + 30);
          const next30DaysStr = toLocalDateString(next30Days);
          
          // Calculate totals from grouped_by_date (these are UPCOMING, not overdue)
          let dueThisWeek = 0;      // Today to end of this week (Sunday)
          let dueNextWeek = 0;      // Next Monday to next Sunday
          let dueThisMonth = 0;     // Payments in current calendar month
          let dueNext30Days = 0;    // All upcoming in next 30 days
          
          Object.keys(upcomingData.grouped_by_date).forEach(dateStr => {
            const amount = (upcomingData.grouped_by_date[dateStr] || []).reduce((s, r) => s + r.approved_amount, 0);
            
            // Due this week: from today to end of this week (Sunday)
            if (dateStr >= todayStr && dateStr <= endOfThisWeekStr) {
              dueThisWeek += amount;
            }
            
            // Due next week: from next Monday to next Sunday
            if (dateStr >= nextWeekStartStr && dateStr <= nextWeekEndStr) {
              dueNextWeek += amount;
            }
            
            // Due this month: payments with due date in current calendar month
            if (dateStr >= startOfMonthStr && dateStr <= endOfMonthStr) {
              dueThisMonth += amount;
            }
            
            // Due next 30 days: all upcoming within 30 days
            if (dateStr >= todayStr && dateStr <= next30DaysStr) {
              dueNext30Days += amount;
            }
          });
          
          // Add overdue to relevant totals
          const overdueTotal = upcomingData.total_overdue || 0;
          const dueThisWeekWithOverdue = dueThisWeek + overdueTotal;
          const dueThisMonthWithOverdue = dueThisMonth + overdueTotal;
          const dueNext30DaysWithOverdue = dueNext30Days + overdueTotal;
          
          return (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {/* Due This Week (includes Overdue) */}
              <Card className={upcomingData.overdue.length > 0 ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className={`w-4 h-4 ${upcomingData.overdue.length > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                    <span className="text-xs text-muted-foreground">Due This Week</span>
                  </div>
                  <p className={`text-xl font-bold mt-1 ${upcomingData.overdue.length > 0 ? 'text-red-600' : 'text-amber-700'}`}>
                    {formatCurrency(dueThisWeekWithOverdue)}
                  </p>
                  {upcomingData.overdue.length > 0 && (
                    <p className="text-xs text-red-500">incl. {formatCurrency(overdueTotal)} overdue</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Due Next Week */}
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Due Next Week</span>
                  </div>
                  <p className="text-xl font-bold mt-1 text-blue-700">
                    {formatCurrency(dueNextWeek)}
                  </p>
                </CardContent>
              </Card>
              
              {/* Due This Month (includes Overdue) */}
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-muted-foreground">Due This Month</span>
                  </div>
                  <p className="text-xl font-bold mt-1 text-purple-700">
                    {formatCurrency(dueThisMonthWithOverdue)}
                  </p>
                </CardContent>
              </Card>
              
              {/* Due Next 30 Days (includes Overdue) */}
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-teal-500" />
                    <span className="text-xs text-muted-foreground">Next 30 Days</span>
                  </div>
                  <p className="text-xl font-bold mt-1 text-teal-700">
                    {formatCurrency(dueNext30DaysWithOverdue)}
                  </p>
                </CardContent>
              </Card>
              
              {/* Total Pending */}
              <Card className="border-gray-300">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1.5">
                    <IndianRupee className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-muted-foreground">Total Pending</span>
                  </div>
                  <p className="text-xl font-bold mt-1">{formatCurrency(upcomingData.total_due)}</p>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : allPayments.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold">All Clear!</h3>
            <p className="text-muted-foreground">No pending payments at the moment</p>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[140px]">Request #</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-[120px]">Due Date</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[80px]">Priority</TableHead>
                    <TableHead className="w-[200px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allPayments.map((request) => (
                    <TableRow 
                      key={request.id} 
                      className={request.isOverdue ? 'bg-red-50 hover:bg-red-100' : ''}
                      data-testid={`payment-row-${request.id}`}
                    >
                      <TableCell className="font-mono text-sm">
                        {request.request_number}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium truncate max-w-[180px]" title={request.supplier_name}>
                            {request.supplier_name}
                          </span>
                          <BankDetailsPopover supplier={suppliersMap[request.supplier_id]} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground truncate max-w-[200px] block" title={request.purpose}>
                          {request.purpose || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate max-w-[120px]" title={request.created_by_name}>
                            {request.created_by_name || '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(request.approved_amount)}
                      </TableCell>
                      <TableCell>
                        {formatDate(request.approved_payment_date)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(request)}
                      </TableCell>
                      <TableCell>
                        {getPriorityBadge(request.priority)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-center">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => openPayDialog(request)}
                            data-testid={`record-payment-btn-${request.id}`}
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            Record
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openModDialog(request)}
                            data-testid={`modify-payment-btn-${request.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
          </TabsContent>

          {/* Aging Report Tab */}
          <TabsContent value="aging" className="space-y-6">
            {/* Aging Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-3">
                  <p className="text-xs text-red-600 font-medium">Total Overdue</p>
                  <p className="text-xl font-bold text-red-700">{formatCurrency(totalOverdueAmount)}</p>
                  <p className="text-xs text-red-500">{totalOverdueCount} payments</p>
                </CardContent>
              </Card>
              {Object.entries(agingData).map(([bucket, data]) => (
                <Card key={bucket} className={data.count > 0 ? 'border-amber-200' : ''}>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground font-medium">{bucket}</p>
                    <p className="text-lg font-bold">{formatCurrency(data.amount)}</p>
                    <p className="text-xs text-muted-foreground">{data.count} payments</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Aging Details Table */}
            {totalOverdueCount === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold">No Overdue Payments</h3>
                  <p className="text-muted-foreground">All payments are up to date</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[140px]">Request #</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Requested By</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-center">Days Overdue</TableHead>
                        <TableHead>Aging Bucket</TableHead>
                        <TableHead className="w-[100px]">Priority</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(agingData).flatMap(([bucket, data]) =>
                        data.items.map(item => (
                          <TableRow key={item.id} className="bg-red-50/50">
                            <TableCell className="font-mono text-sm">
                              {item.request_number}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium truncate max-w-[180px]" title={item.supplier_name}>
                                  {item.supplier_name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm">{item.created_by_name || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-bold text-red-600">
                              {formatCurrency(item.approved_amount)}
                            </TableCell>
                            <TableCell>
                              {formatDate(item.approved_payment_date)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="destructive">{item.daysOverdue} days</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                bucket === '90+ days' ? 'bg-red-100 text-red-700 border-red-300' :
                                bucket === '61-90 days' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                bucket === '31-60 days' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                'bg-yellow-100 text-yellow-700 border-yellow-300'
                              }>
                                {bucket}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {getPriorityBadge(item.priority)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Payment Recording Dialog */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Record Payment
              </DialogTitle>
              <DialogDescription>
                {selectedRequest?.request_number} - {selectedRequest?.supplier_name}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved Amount:</span>
                    <span className="font-bold">{formatCurrency(selectedRequest.approved_amount)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !paymentForm.payment_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {paymentForm.payment_date ? format(new Date(paymentForm.payment_date), 'dd/MM/yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={paymentForm.payment_date ? new Date(paymentForm.payment_date) : undefined}
                          onSelect={(date) => setPaymentForm(prev => ({ ...prev, payment_date: date ? format(date, 'yyyy-MM-dd') : '' }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount Paid (₹) *</Label>
                    <Input
                      type="number"
                      value={paymentForm.amount_paid}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, amount_paid: e.target.value }))}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">Must match approved amount. Use &quot;Modify&quot; to request changes.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Payment Mode *</Label>
                  <Select 
                    value={paymentForm.payment_mode} 
                    onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_mode: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="neft">NEFT</SelectItem>
                      <SelectItem value="rtgs">RTGS</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="dd">Demand Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {paymentForm.payment_mode === 'cheque' || paymentForm.payment_mode === 'dd' ? (
                  <div className="space-y-2">
                    <Label>Cheque/DD Number *</Label>
                    <Input
                      placeholder="Enter cheque or DD number"
                      value={paymentForm.cheque_no}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, cheque_no: e.target.value }))}
                    />
                  </div>
                ) : paymentForm.payment_mode !== 'cash' ? (
                  <div className="space-y-2">
                    <Label>Reference/UTR Number *</Label>
                    <Input
                      placeholder="Enter transaction reference"
                      value={paymentForm.reference_no}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_no: e.target.value }))}
                    />
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    placeholder="Any additional notes..."
                    value={paymentForm.remarks}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, remarks: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleRecordPayment} 
                disabled={processing}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? 'Recording...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modification Request Dialog */}
        <Dialog open={modDialogOpen} onOpenChange={setModDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Request Modification</DialogTitle>
              <DialogDescription>
                Request admin approval to modify this payment
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex justify-between mb-1">
                    <span className="text-muted-foreground">Current Amount:</span>
                    <span className="font-bold">{formatCurrency(selectedRequest.approved_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current Due Date:</span>
                    <span className="font-medium">{formatDate(selectedRequest.approved_payment_date)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>What do you want to modify?</Label>
                  <Select 
                    value={modForm.modification_type} 
                    onValueChange={(v) => setModForm(prev => ({ ...prev, modification_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="change_date">Change Date Only</SelectItem>
                      <SelectItem value="change_amount">Change Amount Only</SelectItem>
                      <SelectItem value="both">Change Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(modForm.modification_type === 'change_amount' || modForm.modification_type === 'both') && (
                  <div className="space-y-2">
                    <Label>New Amount (₹)</Label>
                    <Input
                      type="number"
                      value={modForm.new_amount}
                      onChange={(e) => setModForm(prev => ({ ...prev, new_amount: e.target.value }))}
                    />
                  </div>
                )}

                {(modForm.modification_type === 'change_date' || modForm.modification_type === 'both') && (
                  <div className="space-y-2">
                    <Label>New Due Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !modForm.new_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {modForm.new_date ? format(new Date(modForm.new_date), 'dd/MM/yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={modForm.new_date ? new Date(modForm.new_date) : undefined}
                          onSelect={(date) => setModForm(prev => ({ ...prev, new_date: date ? format(date, 'yyyy-MM-dd') : '' }))}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Reason for Modification *</Label>
                  <Textarea
                    placeholder="Explain why this modification is needed..."
                    value={modForm.reason}
                    onChange={(e) => setModForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setModDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleRequestModification} 
                disabled={processing || !modForm.reason}
              >
                {processing ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
